<script>
	import { page } from '$app/stores';
	import Bubble from '$lib/Components/Layout/Bubble/Bubble.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';

	import headerImage from './images/LP-Beat_The_Heat-001.jpg';
	import lightDiagram from './images/LP-Beat_The_Heat-02.svg';
	import blockHeatNotLight from './images/LP-Beat_The_Heat-03A.svg';
	import airSeal from './images/LP-Beat_The_Heat-03B.svg';
	import energySavings from './images/LP-Beat_The_Heat-03C.svg';
	import affordability from './images/LP-Beat_The_Heat-03D.svg';
	import fan from './images/LP-Beat_The_Heat-04.jpg';
	import installation from './images/LP-Beat_The_Heat-05.jpg';
</script>

<Section
	backgroundColor="blue"
	innerClass={[
		'pt-[clamp(3rem,5vw,6rem)] md:pt-[clamp(2.5rem,5vw,6rem)] pb-[clamp(2.5rem,5vw,6rem)]',
	].join(' ')}
	backgroundImage={headerImage}
	loading="eager"
>
	<h1 class=" font-bold">
		{$page?.data?.h1 ?? $page?.data?.title ?? $page?.url?.pathname}
	</h1>
</Section>
<Section>
	<p>
		This summer, the experts are predicting <a
			href="https://www.forbes.com/sites/brianbushard/2024/05/01/bigger-heat-waves-could-be-in-store-this-summer-as-forecasters-predict-more-above-average-temperatures-heres-where/?sh=e7139f0395c8"
			rel="external"
			target="_blank">higher-than-average temperatures</a
		> across the USA and that means scorching temperatures and skyrocketing energy
		bills.
	</p>
	<p>
		But what if you could keep your home cool and comfortable without cranking
		up the AC all day? Indow window inserts are your secret weapon for staying
		cool and comfortable, all while enjoying the summer sun and saving money on
		your energy bills.
	</p>
	<h2>Indow Window Inserts: Your Summer Comfort Solution</h2>
	<p>
		Indow window inserts are a game-changer for keeping your home cool and
		comfortable all summer long. Here's how they work:
	</p>
	<Image src={lightDiagram} />
</Section>
<Section>
	<ul class="list-none [&_li]:mb-8 max-md:[&_picture]:!col-span-1">
		<li class="grid items-start gap-x-8 gap-y-4 md:grid-cols-[6rem_1fr]">
			<Image src={blockHeatNotLight} class="m-0 max-w-24" />
			<div>
				<h3 class="mb-1 text-left font-bold normal-case text-indow-grey-dark">
					Block Heat, Not Light
				</h3>

				<p>
					Our inserts boast an impressive U-factor of .44, which means they
					significantly resist heat transfer from the outside. Think of it as a
					force field against scorching summer temperatures. At the same time,
					our inserts have an SHGC of .57, allowing ample natural light to fill
					your home without the heat gain associated with traditional windows.
					That's a win-win! Unlike heavy curtains that block all light, Indow
					inserts let you enjoy the beauty of sunshine while staying cool.
				</p>
			</div>
		</li>
		<li class="grid items-start gap-x-8 gap-y-4 md:grid-cols-[6rem_1fr]">
			<Image src={airSeal} class="m-0 max-w-24" />
			<div>
				<h3 class="mb-1 text-left font-bold normal-case text-indow-grey-dark">
					Superior Air Sealing
				</h3>
				<p>
					Air leakage is a major culprit for heat gain. Our patented Snug-Fit
					compression tubing results in an incredibly low air leakage rate of
					.02 cfm/sq ft (cubic feet per minute per square foot). In simpler
					terms, this means they create a near-airtight seal around your
					windows, preventing hot outside air from sneaking in and disrupting
					your cool comfort. The result? Less reliance on your air conditioner
					and significant energy savings!
				</p>
			</div>
		</li>
		<li class="grid items-start gap-x-8 gap-y-4 md:grid-cols-[6rem_1fr]">
			<Image src={energySavings} class="m-0 max-w-24" />
			<div>
				<h3 class="mb-1 text-left font-bold normal-case text-indow-grey-dark">
					Energy Savings & Cost Control
				</h3>
				<p>
					Rising energy costs, particularly for air conditioning, are a growing
					concern. By reducing heat gain and improving efficiency, Indow inserts
					can significantly lower your reliance on AC (as well as lower heating
					costs in the winter). With Indow inserts, you’re not only saving money
					on your energy bills, but helping to save the planet from climate
					change as well.
				</p>
			</div>
		</li>
		<Bubble class="!my-16 mx-auto">
			<span
				class="-mb-2 -ml-6 w-full text-left text-3xl font-normal not-italic text-indow-grey"
				>❝</span
			>
			<blockquote class="mb-0 text-xl font-bold italic">
				Rising energy costs are putting a strain on household budgets. Investing
				in energy-efficient solutions like Indow inserts can help consumers save
				money on their utility bills.
			</blockquote>
			<span
				class="-mr-6 -mt-2 w-full text-right text-3xl font-normal not-italic text-indow-grey"
				>❞</span
			>

			<p class="mb-0 w-full text-right font-extrabold !text-indow-blue">
				<span class="text-indow-grey-dark">–</span> National Energy Assistance Directors
				Association
			</p>
		</Bubble>
		<li class="grid items-start gap-x-8 gap-y-4 md:grid-cols-[6rem_1fr]">
			<Image src={affordability} class="m-0 max-w-24" />
			<div>
				<h3 class="mb-1 text-left font-bold normal-case text-indow-grey-dark">
					Affordability & Investment Value
				</h3>

				<p>
					Replacing windows entirely is a costly and disruptive project. Indow
					inserts offer a much more affordable solution that can also increase
					the value of your home. Plus, Indow inserts are eligible for a federal
					tax credit of 30% (up to $1200) through the Energy Efficient Home
					Improvement Credit, making them an even smarter financial choice.
				</p>
			</div>
		</li>
	</ul>
	<Bubble class="!my-8">
		<p>
			According to <a
				href="https://www.homeadvisor.com/"
				rel="external"
				target="_blank">HomeAdvisor.com</a
			>, the average cost to replace a window is $850 per window, but can be up
			to $2,100!
		</p>
	</Bubble>
</Section>
<Section imageColumn={fan} backgroundColor="grey-dark">
	<h2>More Than Just Summer Relief</h2>
	<p>
		While Indow inserts excel at keeping you cool in summer, they offer
		year-round benefits:
	</p>
	<ul>
		<li>
			<p>
				Reduced Noise Pollution: Indow inserts significantly <a
					href="/solutions/window-noise-reduction"
				>
					reduce outside</a
				> noise, creating a quieter and more peaceful living environment.
			</p>
		</li>

		<li>
			<p>
				Year-Round Comfort: During the winter months, Indow inserts help <a
					href="/solutions/insulating-drafty-windows">retain heat</a
				>, making your home more comfortable and reducing your heating bills.
			</p>
		</li>
		<li>
			<p>
				Block Unwanted Guests: Our air-tight, Snug-Fit system not only keeps air
				from escaping, but blocks pollen, dust, smoke, condensation, and other
				unwanted pollutants.
			</p>
		</li>
	</ul>
</Section>
<Section>
	<h2>Your Investment in a Cooler, More Comfortable Home</h2>
	<p>
		Don't let the summer heat drain your comfort or your wallet. Indow window
		inserts are the smart solution for a cool, comfortable, and energy-efficient
		home. But don’t take our word for it! Here’s some of the glowing 5-star
		reviews from customers who have experienced the Indow difference:
	</p>
	<ul class="grid list-none grid-cols-1 gap-16 md:grid-cols-2">
		<li class="md:col-start-1">
			<h3 class="text-left normal-case">Kristin Faurest</h3>
			<p>⭐⭐⭐⭐⭐</p>
			<p>
				We kept our beautiful old windows while reducing our AC use by a third!
				Which is a lot in our hot Louisville summers! They are also easier to
				handle than heavy storm windows.
			</p>
		</li>
		<li class="md:col-start-2 md:row-span-2">
			<h3 class="text-left normal-case">Christina M</h3>
			<p>⭐⭐⭐⭐⭐</p>
			<p>
				Can’t say enough good things about our Indow inserts. My partner and I
				bought a beautiful Mid Century home in Tucson with original 1958 single
				pane windows. The first couple of brutally hot months were rough, and
				even with our AC running we found ourselves sweating through the night
				and uncomfortable whenever we were standing by the windows.
			</p>
			<p>
				We got our inserts in July and it’s the best purchase we’ve made for the
				house yet. As soon as they were installed you could notice the
				difference in the temperature in our home. To test them out we left one
				room without inserts and then compared the difference in temperature
				using a surface temperature gun. There was a 12 degree difference!
			</p>
			<p>
				We don’t have YoY data, but we compared our usage here to the usage at
				our old rental. That home had a newer AC unit and double pane windows,
				but our energy bill in our home with an older AC and window inserts
				dropped by 27%!
			</p>
			<p>
				We’ve kept them in all year round, but it’s still easy to pop them out
				when we want to air out a room. On top of the energy savings we also got
				a $1,000 credit on our taxes!
			</p>
		</li>
		<li class="md:col-start-1">
			<h3 class="text-left normal-case">ryan hladky</h3>
			<p>⭐⭐⭐⭐⭐</p>
			<p>
				I've had these window inserts for about a year and they work great. The
				noise reduction is very noticable and it really seals off my drafty
				windows. I noticed my air conditioner didn't have to work as hard last
				summer, the house stayed a more even temperature, and I had a reduction
				in energy usage.
			</p>
			<Image src={installation} />
		</li>
	</ul>
</Section>

<GetAFreeEstimate />
